import React from 'react';
import { Box, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { INewsReponse } from '../../../model/base/response/news-response';
import dayjs from 'dayjs';
import { useNavigate } from '../../../hooks/useNavigate';
import { useParams } from 'react-router-dom';
import { clickableCardSx, RADIUS, tabularNums } from '../../../theme/tokens';

interface NewsCardProps {
  news: INewsReponse;
}

const newsDate = (news: INewsReponse) => dayjs(news.publishedDate || news.date).format('DD/MM/YYYY');

/** Link da notícia com href real (abrir em nova aba, copiar link) e navegação SPA no clique. */
function useNewsLink(news: INewsReponse) {
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const path = `/noticia/${news.id}`;
  const href = params.state && params.city ? `/${params.state}/${params.city}${path}` : path;

  return {
    href,
    onClick: (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      navigate(path);
    },
  };
}

/** Imagem da notícia com proporção fixa; sem imagem ou com erro mostra um placeholder neutro. */
function NewsImage({ news, ratio, radius = 0, eager = false }: { news: INewsReponse; ratio: string; radius?: number; eager?: boolean }) {
  const [imageError, setImageError] = React.useState(false);
  const hasImage = !!news.urlImageEmphasis && !imageError;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: ratio,
        overflow: 'hidden',
        borderRadius: `${radius}px`,
        backgroundColor: 'grey.100',
        flexShrink: 0,
      }}
    >
      {hasImage ? (
        <Box
          component="img"
          src={news.urlImageEmphasis}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          onError={() => setImageError(true)}
          className="news-image"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      ) : (
        <Box
          aria-hidden
          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'grey.400' }}
        >
          <ImageOutlinedIcon sx={{ fontSize: '2.5rem' }} />
        </Box>
      )}
    </Box>
  );
}

function NewsMeta({ news }: { news: INewsReponse }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {news.category && (
        <Typography variant="overline" component="span" sx={{ color: 'brand.text', lineHeight: 1.4 }}>
          {news.category}
        </Typography>
      )}
      {news.category && <Box component="span" aria-hidden sx={{ color: 'text.secondary' }}>·</Box>}
      <Typography variant="caption" component="time" color="text.secondary" sx={tabularNums}>
        {newsDate(news)}
      </Typography>
    </Box>
  );
}

const clampSx = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

/** Card vertical (listagem de notícias). */
export default function NewsCard({ news }: NewsCardProps) {
  const link = useNewsLink(news);

  return (
    <Box
      component="a"
      href={link.href}
      onClick={link.onClick}
      sx={{
        ...(clickableCardSx as object),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&:hover .news-image': { transform: 'scale(1.03)' },
      }}
    >
      <NewsImage news={news} ratio="16 / 10" />
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
        <NewsMeta news={news} />
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.0625rem', lineHeight: 1.4, ...clampSx(3) }}>
          {news.title}
        </Typography>
      </Box>
    </Box>
  );
}

/** Notícia em destaque da home: imagem 16:9 e título grande. */
export function NewsFeatureCard({ news }: NewsCardProps) {
  const link = useNewsLink(news);

  return (
    <Box
      component="a"
      href={link.href}
      onClick={link.onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        color: 'inherit',
        textDecoration: 'none',
        '&:hover .news-image': { transform: 'scale(1.03)' },
        '&:hover .news-title': { color: 'brand.text' },
      }}
    >
      <NewsImage news={news} ratio="16 / 9" radius={RADIUS.card} eager />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <NewsMeta news={news} />
        <Typography
          variant="h3"
          component="h3"
          className="news-title"
          sx={{ fontSize: { xs: '1.25rem', md: '1.625rem' }, transition: 'color 200ms', ...clampSx(3) }}
        >
          {news.title}
        </Typography>
      </Box>
    </Box>
  );
}

/** Item compacto da lista lateral da home: miniatura + título + data. */
export function NewsListItem({ news }: NewsCardProps) {
  const link = useNewsLink(news);

  return (
    <Box
      component="a"
      href={link.href}
      onClick={link.onClick}
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        py: 2,
        color: 'inherit',
        textDecoration: 'none',
        '&:hover .news-title': { color: 'brand.text' },
        '&:hover .news-image': { transform: 'scale(1.05)' },
      }}
    >
      <Box sx={{ width: { xs: 96, sm: 128 }, flexShrink: 0 }}>
        <NewsImage news={news} ratio="4 / 3" radius={RADIUS.control + 2} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <NewsMeta news={news} />
        <Typography
          variant="h6"
          component="h3"
          className="news-title"
          sx={{ fontSize: '1rem', lineHeight: 1.4, transition: 'color 200ms', ...clampSx(3) }}
        >
          {news.title}
        </Typography>
      </Box>
    </Box>
  );
}
